import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RowActions } from "@/components/dashboard/RowActions";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const EMPTY = { name: "", description: "", is_active: true };

export default function Industries() {
  const [rows, setRows] = useState([]);
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(rows, 20);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = async () => setRows((await api.get("/industries")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditingId(r.industry_id); setF({ name: r.name, description: r.description, is_active: r.is_active }); setOpen(true); };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    try {
      if (editingId) await api.put(`/industries/${editingId}`, f);
      else await api.post("/industries", f);
      toast.success(editingId ? "Industry updated" : "Industry added");
      setOpen(false); setF(EMPTY); setEditingId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    try { await api.delete(`/industries/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="industries-admin-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow text-navy/50">Admin</div>
          <h1 className="serif-display text-4xl mt-2">Industries we serve</h1>
          <p className="text-navy/60 mt-2">Manage the industries offered on the public site and used in client onboarding forms.</p>
        </div>
        <button className="btn-primary" onClick={openCreate} data-testid="new-industry-btn"><Plus size={14}/> New industry</button>
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-navy/50 py-8">No industries yet.</TableCell></TableRow>}
            {pageItems.map((r) => (
              <TableRow key={r.industry_id}>
                <TableCell className="serif-display text-lg">{r.name}</TableCell>
                <TableCell className="text-navy/70 max-w-lg">{r.description || "—"}</TableCell>
                <TableCell><Badge className={r.is_active ? "bg-navy text-gold hover:bg-navy" : "bg-slateBg text-navy/60"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell>
                  <RowActions
                    onEdit={() => openEdit(r)}
                    onDelete={() => del(r.industry_id)}
                    editTestid={`edit-industry-${r.industry_id}`}
                    deleteTestid={`delete-industry-${r.industry_id}`}
                    itemLabel={r.name}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="industries-table" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="serif-display text-2xl">{editingId ? "Edit industry" : "Add industry"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} data-testid="industry-name"/></div>
            <div><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e)=>setF({...f, description: e.target.value})}/></div>
            <div className="flex items-center justify-between border border-navy/15 p-3">
              <div>
                <div className="label-eyebrow text-navy/60">Visible on public site</div>
                <div className="text-xs text-navy/50 mt-1">Inactive industries are hidden from applicants but stay in the DB.</div>
              </div>
              <Switch checked={f.is_active} onCheckedChange={(v)=>setF({...f, is_active: v})} data-testid="industry-active"/>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="industry-save-submit">{editingId ? "Save changes" : "Add industry"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
